import React, { useEffect, useState, useRef } from "react";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ZoomModal from "./heatmap/zoomModal";
import { overallParticipationJobLevel } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import { API_RESPONSE } from "../utills/app-constant";
import Modal from "../admin/components/common/Model";
import Remark from "../admin/components/Charts/Remark";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";

const OverallParticipationChartJobLevel = ({
  selectedCompanyId,
  graphName,
  selectedCampaignId,
  type,
  title = "Overall Participation (Job Level)",
}) => {
  const chartRef = useRef();
  const modalRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [remarkId, setRemarkId] = useState("");

  const modalId = `zoomModal-${title.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        id: "job_level",
        type: "bar",
        stacked: true,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: { horizontal: true, barHeight: "35%" },
      },
      colors: ["#28a745", "#ffc107", "#dc3545"],
      xaxis: { categories: [] },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 10,
        labels: { formatter: (value) => value },
      },
      dataLabels: { enabled: true },
      legend: {
        position: "top",
        onItemClick: { toggleDataSeries: false },
      },
    },
  });

  /** ✅ Fetch Chart Data */
  const getAllParticipationDivisionData = async () => {
    setLoading(true);
    try {
      const response = await overallParticipationJobLevel(
        selectedCompanyId,
        selectedCampaignId,
        type,
        graphName
      );

      if (response?.data?.responseCode === 200) {
        const data = response.data.hierarchyStats || [];
        setRemarkText(response.data.remark || "");
        setRemarkId(response.data.remarkId || "");
        // Defensive check for array length
        if (!Array.isArray(data) || data.length === 0) {
          setDepartmentStats([]);
          setChartData((prev) => ({
            ...prev,
            series: [],
            options: { ...prev.options, xaxis: { categories: [] } },
          }));
          setLoading(false);
          return;
        }

        const departmentNames = data.map((d) => d.hierarchyName || "N/A");
        const Completed = data.map((d) => d.attemptedEmployee || 0);
        const InProgress = data.map((d) => d.inProgressEmployee || 0);
        const NotStarted = data.map((d) => d.notAttemptedEmployee || 0);

        setChartData({
          series: [
            { name: "Completed", data: Completed },
            { name: "In Progress", data: InProgress },
            { name: "Not Started", data: NotStarted },
          ],
          options: {
            ...chartData.options,
            xaxis: { categories: departmentNames },
          },
        });

        setDepartmentStats(data);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId && selectedCampaignId) {
      getAllParticipationDivisionData();
    }
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);

  /**  Export Excel */
  const exportAsExcel = () => {
    if (!departmentStats.length) {
      toastEmitter("warning", "No data to export!");
      return;
    }
    const headers = [
      "Hierarchy",
      "Total",
      "Not Started",
      "Completed",
      "In Progress",
    ];
    const rows = departmentStats.map((d) => [
      d.hierarchyName,
      d.totalEmployee,
      d.notAttemptedEmployee,
      d.attemptedEmployee,
      d.inProgressEmployee,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Participation");
    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  /** Export PDF */
   const exportAsPDF = async () => {
  const chartBox = document.getElementById(`chart-box-${title}`);
  if (!chartBox) return;
  chartBox.scrollTo(0, 0);
  await new Promise(r => setTimeout(r, 200));
 
  const chartElement = chartBox.querySelector(".apexcharts-canvas");
  if (!chartElement) return;
 
  const canvas = await html2canvas(chartElement, {
    scale: 2,
    backgroundColor: "#fff",
    useCORS: true,
  });
 
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("l", "mm", "a4");
 
  const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
  const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF
 
  const canvasWidth = canvas.width; // Width of the canvas image
  const canvasHeight = canvas.height; // Height of the canvas image
 
  // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
  const scaleFactor = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%
 
  const imgWidth = canvasWidth * scaleFactor; // Scaled width
  const imgHeight = canvasHeight * scaleFactor; // Scaled height
 
  // Calculate the offset to center the image
  const xOffset = (pdfWidth - imgWidth) / 2;
  const yOffset = (pdfHeight - imgHeight) / 2;
 
  // Add the image to the PDF, centered and scaled down
  pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
 
  // Save the PDF
  pdf.save(`${title || "chart"}.pdf`);
};

const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };
 const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div id="chart-gap-container" className="card h-100 shadow-sm">
      {/* Header Toolbar */}
      <div className="d-flex justify-content-between align-items-center overall-text p-2">
        <h5 className="fw-semibold mb-0">{title}</h5>
        <div className="d-flex align-items-center gap-2">
          <button
            className="border-0 bg-transparent btn-sm"
            title="Zoom"
            data-bs-toggle="modal"
            data-bs-target={`#${modalId}`}
          >
            <img src={ZOOM_ICON} alt="zoom" />
          </button>

          <div className="dropdown">
            <button
              className="bg-transparent icon-btn"
              data-bs-toggle="dropdown"
            >
              <img src={MENU_ICON} alt="menu" />
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button className="dropdown-item" onClick={exportAsExcel}>
                  Download Excel
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportAsPDF}>
                  Export PDF
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportAsImage}>
                  Download Image
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Chart Section */}
              <div id={`chart-box-${title}`} className="overallParticipation">
      {loading ? (
        <div className="text-center py-5">Loading...</div>
      ) : (
        <div ref={chartRef}>
          <Chart
            options={chartData.options}
            series={chartData.series}
            type="bar"
            width="100%"
            height={350}
          />
        </div>
      )}
</div>
      {/* Chat / Remark Icon */}
      <button
        title="Add Comment"
        onClick={() => {
          setShowRemarkModal(true);
          setTimeout(() => {
            if (modalRef.current) modalRef.current.click();
          }, 0);
        }}
        className="border-0 bg-transparent position-absolute"
        style={{
          bottom: "10px",
          right: "10px",
        }}
      >
        <img src={CHAT_ICON} alt="chat" />
      </button>

      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Remark" buttonLable="">
          <Remark
                          selectedCompanyId={selectedCompanyId}
                          selectedCampaignId={selectedCampaignId}
                          type={type}
                          graphName={graphName}
                          initialRemark={remarkText}
                          remarkId={remarkId}
                          isAttempt={0}
                          setRemarkprops={setRemarkText}
                          setRemarkIdProps={setRemarkId}
                          onCloseModal={() => setShowRemarkModal(false)}
                        />

        </Modal>
      )}

      {/* Zoom Modal */}
      <ZoomModal
        id={modalId}
        title={title}
        chartOptions={chartData.options}
        seriesData={chartData.series}
        type="bar"
        height={400}
        remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
      />
    </div>
  );
};

export default OverallParticipationChartJobLevel;
